import { Grid } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useHatDbContext } from '../../context/HatDbContext';
import { useHatDbDataContext } from '../../context/HatDbDataContext';
import { HatMetric, hatMetrics, PRIORTY_CURRENT, PRIORTY_PREVIOUS } from '../../HatMetrics';
import { useHatQuery } from '../../HatPriorityQueryQueue';
import { sumIfAllNotNull } from '../../HatUtils';
import { HatDbVisualActionAttributionPieChartCard } from '../widgets/HatDbVisualActionAttributionPieChartCard';
import { HatDbVisualActionAttributionTable } from '../widgets/HatDbVisualActionAttributionTable';

export type HatMetricActionAttributionCount = {
    count: number | null;
    adInfluencedCount: number | null;
};

type PageViewsByCategoryResult = {
    pageViewService: HatMetricActionAttributionCount;
    pageViewContact: HatMetricActionAttributionCount;
};

type PageViewsByContentResult = {
    pageViewSrp: HatMetricActionAttributionCount;
    pageViewVdp: HatMetricActionAttributionCount;
};

type PageViewsSpecialResult = {
    pageViewSpecial: HatMetricActionAttributionCount;
};

type ClicksByCategoryResult = {
    clicksPhone: HatMetricActionAttributionCount;
};

type FormSubmissionsResult = {
    formSubmit: HatMetricActionAttributionCount;
};

type TracksByEventNameResult = {
    copiedPhone: HatMetricActionAttributionCount;
};

export const hatActionsFriendlyNames: { [key: string]: string } = {
    clicksPhone: 'Click to Calls',
    copiedPhone: 'Copied Phone Number',
    formSubmit: 'Form Submissions',
    pageViewService: 'Service Page Views',
    pageViewContact: 'Total Hours and Directions Views',
    pageViewSrp: 'SRP Views',
    pageViewVdp: 'VDP Views',
    pageViewSpecial: 'Specials Page Views',
} as const;

export type HatMetricActionAttributionData = PageViewsByCategoryResult &
    PageViewsByContentResult &
    PageViewsSpecialResult &
    ClicksByCategoryResult &
    FormSubmissionsResult &
    TracksByEventNameResult & {
        total: HatMetricActionAttributionCount;
    };

const pageViewsByCategory: HatMetric = {
    ...hatMetrics.pageViewsByCategory,
    select: (data: unknown): PageViewsByCategoryResult => {
        const rows = data as any[];
        const serviceRow = rows.find((r) => r.page_category === 'service');
        const contactRow = rows.find((r) => r.page_category === 'hours');
        const hoursRow = rows.find((r) => r.page_category === 'contact');
        return {
            pageViewService: {
                count: serviceRow?.page_view_count ?? 0,
                adInfluencedCount: serviceRow?.ad_influenced_page_view_count ?? 0,
            },
            pageViewContact: {
                count: (contactRow?.page_view_count ?? 0) + (hoursRow?.page_view_count ?? 0),
                adInfluencedCount:
                    (contactRow?.ad_influenced_page_view_count ?? 0) + (hoursRow?.ad_influenced_page_view_count ?? 0),
            },
        };
    },
};

const pageViewsByContent: HatMetric = {
    ...hatMetrics.pageViewsByContent,
    select: (data: unknown): PageViewsByContentResult => {
        const rows = data as any[];
        const srpRow = rows.find((r) => r.page_content === 'srp');
        const vdpRow = rows.find((r) => r.page_content === 'vdp');
        return {
            pageViewSrp: {
                count: srpRow?.page_view_count ?? 0,
                adInfluencedCount: srpRow?.ad_influenced_page_view_count ?? 0,
            },
            pageViewVdp: {
                count: vdpRow?.page_view_count ?? 0,
                adInfluencedCount: vdpRow?.ad_influenced_page_view_count ?? 0,
            },
        };
    },
};

const pageViewsSpecial: HatMetric = {
    ...hatMetrics.pageViewsSpecial,
    select: (data: unknown): PageViewsSpecialResult => {
        const specialRow = (data as any[])[0];
        return {
            pageViewSpecial: {
                count: specialRow?.page_view_count ?? 0,
                adInfluencedCount: specialRow?.ad_influenced_page_view_count ?? 0,
            },
        };
    },
};

const clicksByCategory: HatMetric = {
    ...hatMetrics.clicksByCategory,
    select: (data: unknown): ClicksByCategoryResult => {
        const rows = data as any[];
        const phoneRow = rows.find((r) => r.click_category === 'phone');
        return {
            clicksPhone: {
                count: phoneRow?.click_count ?? 0,
                adInfluencedCount: phoneRow?.ad_influenced_click_count ?? 0,
            },
        };
    },
};

const formSubmissions: HatMetric = {
    ...hatMetrics.formSubmissions,
    select: (data: unknown): FormSubmissionsResult => {
        const formSubmitRow = (data as any[])[0];
        return {
            formSubmit: {
                count: formSubmitRow?.event_count ?? 0,
                adInfluencedCount: formSubmitRow?.ad_influenced_event_count ?? 0,
            },
        };
    },
};

const tracksByEventName: HatMetric = {
    ...hatMetrics.tracksByEventName,
    select: (data: unknown): TracksByEventNameResult => {
        const rows = data as any[];
        const copiedPhoneRow = rows.find((r) => r.event_name === 'copied_phone');
        return {
            copiedPhone: {
                count: copiedPhoneRow?.event_count ?? 0,
                adInfluencedCount: copiedPhoneRow?.ad_influenced_event_count ?? 0,
            },
        };
    },
};

const actionsMetrics = [
    clicksByCategory,
    formSubmissions,
    pageViewsByCategory,
    pageViewsByContent,
    pageViewsSpecial,
    tracksByEventName,
];

export default function HatDbSectionActionAttribution(props: { dataContextKey: string }) {
    const hatDbContext = useHatDbContext();
    const hatDbDataContext = useHatDbDataContext();

    // Get the data from the queries.
    const currentCriteria = hatDbContext.getCurrentRangeCriteria();
    const previousCriteria = hatDbContext.getPreviousRangeCriteria();
    const currentQueries: UseQueryResult[] = [];
    const previousQueries: UseQueryResult[] = [];
    for (const hatMetric of actionsMetrics) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const currentQuery = useHatQuery(
            {
                ...hatMetric,
                ...currentCriteria,
                priority: PRIORTY_CURRENT,
            },
            true
        );
        currentQueries.push(currentQuery);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const previousQuery = useHatQuery(
            {
                ...hatMetric,
                ...previousCriteria!,
                priority: PRIORTY_PREVIOUS,
            },
            !!previousCriteria
        );
        previousQueries.push(previousQuery);
    }

    const { currentData, previousData } = useMemo(
        () => {
            // Create empty objects to hold the data.
            const currentData: HatMetricActionAttributionData = {
                clicksPhone: {
                    count: null,
                    adInfluencedCount: null,
                },
                copiedPhone: {
                    count: null,
                    adInfluencedCount: null,
                },
                formSubmit: {
                    count: null,
                    adInfluencedCount: null,
                },
                pageViewService: {
                    count: null,
                    adInfluencedCount: null,
                },
                pageViewContact: {
                    count: null,
                    adInfluencedCount: null,
                },
                pageViewSrp: {
                    count: null,
                    adInfluencedCount: null,
                },
                pageViewVdp: {
                    count: null,
                    adInfluencedCount: null,
                },
                pageViewSpecial: {
                    count: null,
                    adInfluencedCount: null,
                },
                total: {
                    count: null,
                    adInfluencedCount: null,
                },
            };
            const previousData: HatMetricActionAttributionData = structuredClone(currentData);

            for (let i = 0; i < currentQueries.length; i++) {
                if (!currentQueries[i].isPending && !currentQueries[i].isError) {
                    Object.assign(currentData, currentQueries[i].data);
                }
                if (!previousQueries[i].isPending && !previousQueries[i].isError) {
                    Object.assign(previousData, previousQueries[i].data);
                }
            }

            // Sum the results to get the totals.
            for (const intervalData of [currentData, previousData]) {
                intervalData.total.count = sumIfAllNotNull(
                    intervalData.pageViewService.count,
                    intervalData.pageViewContact.count,
                    intervalData.pageViewSrp.count,
                    intervalData.pageViewVdp.count,
                    intervalData.pageViewSpecial.count,
                    intervalData.clicksPhone.count,
                    intervalData.copiedPhone.count,
                    intervalData.formSubmit.count
                );

                intervalData.total.adInfluencedCount = sumIfAllNotNull(
                    intervalData.pageViewService.adInfluencedCount,
                    intervalData.pageViewContact.adInfluencedCount,
                    intervalData.pageViewSrp.adInfluencedCount,
                    intervalData.pageViewVdp.adInfluencedCount,
                    intervalData.pageViewSpecial.adInfluencedCount,
                    intervalData.clicksPhone.adInfluencedCount,
                    intervalData.copiedPhone.adInfluencedCount,
                    intervalData.formSubmit.adInfluencedCount
                );
            }

            return {
                currentData,
                previousData,
            };
        },
        // Only calculate the data from the queries in this memo.
        // Do not add anything that could introduce new dependencies.
        // See HatMetricChannelAttribution.tsx for a non-array based implementation of this pattern.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            // eslint-disable-next-line react-hooks/exhaustive-deps
            ...currentQueries.map((q) => q.isPending),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            ...currentQueries.map((q) => q.isError),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            ...currentQueries.map((q) => q.data),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            ...previousQueries.map((q) => q.isPending),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            ...previousQueries.map((q) => q.isError),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            ...previousQueries.map((q) => q.data),
        ]
    );

    useEffect(() => {
        // Publish the totals on the dashboard data context.
        hatDbDataContext?.setData(`${props.dataContextKey}`, 'current', currentData);
        hatDbDataContext?.setData(`${props.dataContextKey}`, 'previous', previousData);
    }, [currentData, previousData, hatDbDataContext, props.dataContextKey]);

    return (
        <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
                <HatDbVisualActionAttributionPieChartCard currentData={currentData} previousData={previousData} />
            </Grid>

            <Grid item md={6} xs={12}>
                <HatDbVisualActionAttributionTable currentData={currentData} previousData={previousData} />
            </Grid>
        </Grid>
    );
}
